import { gql, useLazyQuery, useMutation } from '@apollo/client'
import { useEffect, useState } from 'react'

import {
  ConsoleItems,
  ConsoleItemsQuery,
  ConsoleItemsQueryVariables,
  ConsoleMissionBoxFragmentDoc,
  CreateMissionMutation,
  CreateMissionMutationVariables,
  GpsDataInput,
  Maybe,
  MissionType,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import {
  useMessage,
  useRouter,
  useTranslation,
  useUserTmpUploader,
} from '@/core'
import { makeViewModel } from '@/core/architecture'
import { useCurrentUser } from '@/core/auth'
import { Camera } from '@/core/native'
import { useLocationWatcher } from '@/core/native/geolocation'
import {
  getDetail,
  getPlacePredictions,
  Place,
  Prediction,
} from '@/lib/googleMap'
import { createUuid } from '@/lib/uuid'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { t } = useTranslation('consoleMissionCreate')
  const { showError, showMessage } = useMessage()
  const { user } = useCurrentUser()
  const { router, routerPages } = useRouter<{ missionType: MissionType }>()
  const [predictions, setPredictions] = useState<Prediction[]>([])
  const [place, setPlace] = useState<Place | null>(null)
  const { getImage } = Camera
  const { upload, progress } = useUserTmpUploader(user?.uid || '')
  const { permission, currentLocation, requestPermission, watch, unwatch } =
    useLocationWatcher()

  const [getItems, { data: items, loading }] = useLazyQuery<
    ConsoleItemsQuery,
    ConsoleItemsQueryVariables
  >(ConsoleItems)

  const [createMission] = useMutation<
    CreateMissionMutation,
    CreateMissionMutationVariables
  >(CREATE_MISSION, {
    onCompleted() {
      showMessage(t('createMissionSuccess'), 'success')
      router.goBack(routerPages.ConsoleMissions)
    },
  })

  const getResource = async () => {
    const file = await getImage(config.upload.mimeTypes.image)
    if (file.size > config.upload.limit['image']) {
      throw new Error('uploadErrorMaximumSize')
    }
    return file
  }

  useEffect(() => {
    if (!permission) return
    watch().catch(showError)
    return () => {
      unwatch().catch(showError)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permission, unwatch, watch])

  return {
    state: {
      artistId: user?.uid ?? '',
      missionType: router.query.missionType,
      progress,
      loading,
      hasNextPage: !!items?.artistItems?.pageInfo.hasNextPage,
      items: items?.artistItems ?? null,
      predictions,
      place,
      gpsClientInfo: {
        permission,
        currentLocation,
      },
    },
    actions: {
      async onSubmit({
        title,
        description,
        type,
        expiredAt,
        startedAt,
        content,
        reward,
        gpsData,
      }) {
        await createMission({
          variables: {
            inputs: {
              title,
              description,
              type,
              isPublished: true,
              startedAt: startedAt,
              expiredAt: expiredAt,
              content,
              reward,
              gpsData: buildGpsData(gpsData),
            },
          },
        })
      },
      async onDrop() {
        const file = await getResource().catch(error => {
          showError(new Error(t([error.message, 'interruptedUpload'])))
        })
        if (!file) {
          return
        }
        const { tmpPath, fileUrl } = await upload(file)
        const uuid = createUuid()
        return {
          id: uuid,
          resourceName: uuid,
          imageUrl: fileUrl,
          imageUploadPath: tmpPath,
        }
      },
      async onFetchMore() {
        await getItems({
          variables: {
            pageInput: { cursor: items?.artistItems?.pageInfo.endCursor },
          },
        })
      },
      async onSearch() {
        await getItems()
      },
      async onPlaceSearch({ keyword }) {
        if (!keyword) {
          return
        }
        const predictions = await getPlacePredictions(keyword)
        setPredictions(predictions)
      },
      async onSelectPlace({ placeId, map }) {
        const result = await getDetail({ placeId, map })
        if (result.isErr()) {
          throw result.err()
        }
        setPlace(result.unwrap())
      },
      removePlace() {
        setPlace(null)
      },
      async onRequestGPSPermission() {
        if (permission) return
        await requestPermission()
      },
    },
  }
})

const CREATE_MISSION = gql`
  mutation CreateMission($inputs: CreateMissionInput!) {
    createMission(inputs: $inputs) {
      ...ConsoleMissionBox
    }
  }
  ${ConsoleMissionBoxFragmentDoc}
`

export function buildGpsData(
  gpsData?: Maybe<Omit<GpsDataInput, 'radius'> & { radius: string | number }>
): Maybe<GpsDataInput> | undefined {
  if (!gpsData?.radius) {
    return undefined
  }
  return {
    ...gpsData,
    radius: toNumber(String(gpsData.radius)),
  }
}

function toNumber(string: string): number {
  return +(string.replace(/[a-z\s]*/g, '') ?? 0)
}
